// iter510 — CoachNudgeBell
// Proactive AI Coach nudges popover. Bell icon with badge counter in HealthHub.
// Click → opens dropdown panel with up to 3 nudges, each with:
//   • Title + body (PMID-cited reasoning)
//   • "Dismiss" + "Took action" buttons (drives engagement metric)
//
// Backend: GET /api/coach/nudges, POST /api/coach/nudges/{dismiss,act}
// Rules: rule-based, no LLM credits, idempotent (won't repeat same nudge in 24h)

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Bell, X, Check, AlertCircle, Loader2 } from "lucide-react";

const SEVERITY_TONE = {
  high:   { light: "bg-rose-100 text-rose-700",     dark: "bg-rose-500/15 text-rose-300" },
  medium: { light: "bg-amber-100 text-amber-700",   dark: "bg-amber-500/15 text-amber-300" },
  low:    { light: "bg-violet-100 text-violet-700", dark: "bg-violet-500/15 text-violet-300" },
};

export default function CoachNudgeBell() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language !== "en";

  const [nudges, setNudges] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);

  const fetchNudges = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/coach/nudges?lang=${bg ? "bg" : "en"}`);
      setNudges(data.nudges || []);
    } catch {
      setNudges([]);
    } finally { setLoading(false); }
  }, [API, user, bg]);

  useEffect(() => { fetchNudges(); }, [fetchNudges]);

  const dismiss = async (id) => {
    setActingId(id);
    try {
      await axios.post(`${API}/coach/nudges/dismiss`, { id });
      setNudges((prev) => prev.filter((n) => n.id !== id));
    } finally { setActingId(null); }
  };

  const act = async (id) => {
    setActingId(id);
    try {
      await axios.post(`${API}/coach/nudges/act`, { id });
      const route = nudges.find((n) => n.id === id)?.route;
      setNudges((prev) => prev.filter((n) => n.id !== id));
      if (route) { setOpen(false); navigate(route); }
    } finally { setActingId(null); }
  };

  const count = nudges.length;
  if (!user) return null;

  return (
    <div className="relative inline-block" data-testid="coach-nudge-bell-wrapper">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="coach-nudge-bell"
        aria-label="Coach nudges"
        className={`relative w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:-translate-y-0.5 ${
          isDark ? "bg-slate-900 border border-slate-800 hover:bg-slate-800" : "bg-white border border-slate-200 hover:bg-slate-50"
        }`}
      >
        <Bell className={`w-4 h-4 ${count > 0 ? "text-violet-500" : (isDark ? "text-slate-400" : "text-slate-500")}`} />
        {count > 0 && (
          <span
            data-testid="coach-nudge-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-outside backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Popover */}
          <div
            data-testid="coach-nudge-panel"
            className={`absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-y-auto rounded-2xl shadow-2xl z-50 ${
              isDark ? "bg-slate-950 border border-slate-800" : "bg-white border border-slate-200"
            }`}
          >
            <div className={`px-4 py-3 sticky top-0 ${isDark ? "bg-slate-950 border-b border-slate-800" : "bg-white border-b border-slate-100"}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  {bg ? "AI Coach сигнали" : "AI Coach insights"}
                </h3>
                <button
                  onClick={() => setOpen(false)}
                  className={`w-6 h-6 rounded-md flex items-center justify-center ${isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className={`text-[10px] mt-0.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {bg ? "Проактивни предложения базирани на твоите данни" : "Proactive suggestions based on your data"}
              </p>
            </div>

            <div className="p-3 space-y-2">
              {loading && (
                <div className={`flex items-center gap-2 text-xs py-4 justify-center ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {bg ? "Анализирам данните…" : "Analyzing your data…"}
                </div>
              )}
              {!loading && nudges.length === 0 && (
                <div className={`text-xs py-6 text-center ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  <Check className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
                  {bg ? "Няма нови сигнали — браво!" : "No new insights — great work!"}
                </div>
              )}
              {nudges.map((n) => {
                const tone = (SEVERITY_TONE[n.severity] || SEVERITY_TONE.low)[isDark ? "dark" : "light"];
                return (
                  <div
                    key={n.id}
                    data-testid={`coach-nudge-${n.rule_id}`}
                    className={`p-3 rounded-xl border ${isDark ? "bg-slate-900 border-slate-800" : "bg-slate-50 border-slate-200"}`}
                  >
                    <div className="flex items-start gap-2 mb-1.5">
                      <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        n.severity === "high" ? "text-rose-500" :
                        n.severity === "medium" ? "text-amber-500" : "text-violet-500"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {n.title}
                        </div>
                        <span className={`inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${tone}`}>
                          {n.severity}
                        </span>
                      </div>
                    </div>
                    <p className={`text-[11px] leading-relaxed mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                      {n.body}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => act(n.id)}
                        disabled={actingId === n.id}
                        data-testid={`coach-nudge-act-${n.rule_id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 h-7 rounded-lg text-[10.5px] font-bold bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {actingId === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        {n.action_label || (bg ? "Приемам" : "I accept")}
                      </button>
                      <button
                        onClick={() => dismiss(n.id)}
                        disabled={actingId === n.id}
                        data-testid={`coach-nudge-dismiss-${n.rule_id}`}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-[10.5px] ${
                          isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-400" : "bg-slate-200 hover:bg-slate-300 text-slate-600"
                        }`}
                        aria-label="Dismiss"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
